import { next } from '@ember/runloop';
import { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';

import type { DateRangeInterface, DSSetup, RecordData, Service } from 'webapp-types';
import type { ChangeIndicatorClass } from 'webapp/enums/enums';
import {
	BlockVisualization,
	BlockVisualizationSpecial,
	ModelName,
	TeamSectionItemType,
} from 'webapp/enums/enums';
import { copyObject, equal } from 'webapp/utils/common';

import type Block from 'webapp/models-custom/block';
import type Board from 'webapp/models-custom/board';
import type BoardLoop from 'webapp/models-custom/board-loop';
import BufferedModel from 'webapp/models-custom/buffer/buffered-model';
import type DataSelect from 'webapp/models-custom/data-select';
import DateRange from 'webapp/models-custom/date-range';
import type Goal from 'webapp/models-custom/goal';
import MetricLimitations from 'webapp/models-custom/metric-limitations';
import type { Props as MetricSetupProps } from 'webapp/models-custom/metric-setup';
import MetricSetup from 'webapp/models-custom/metric-setup';
import type {
	OnChangeProp as MetricSetupOnChangeProp,
	OnChangePropValue as MetricSetupOnChangePropValue,
} from 'webapp/models-custom/metric-setup-base';
import type Report from 'webapp/models-custom/report';
import type Source from 'webapp/models-custom/source';
import type VisualizationLineChart from 'webapp/models-custom/visualization/line-chart';
import type TeamSectionItemRecord from 'webapp/models/team-section-item';

export default class TeamSectionItem extends BufferedModel<
	TeamSectionItemRecord,
	RecordData.TeamSectionItem.Serialized
> {
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('model-manager/board-loops')
	declare mmBoardLoopsService: Service.ModelManager.BoardLoops;
	@service('model-manager/reports') declare mmReportsService: Service.ModelManager.Reports;
	@service('model-manager/goals') declare mmGoalsService: Service.ModelManager.Goals;
	@service('billing') declare billingService: Service.Billing;
	@service('model-manager/team-sections')
	declare mmTeamSectionsService: Service.ModelManager.TeamSections;
	@service('model-manager/blocks') declare mmBlocksService: Service.ModelManager.Blocks;
	@service('timer') declare timerService: Service.Timer;
	@service('logger') declare loggerService: Service.Logger;
	@service('model-manager/teams') declare mmTeamsService: Service.ModelManager.Teams;
	@service('account') declare accountService: Service.Account;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('model-manager/team-section-items')
	declare mmTeamSectionItemsService: Service.ModelManager.TeamSectionItems;

	get id(): string | null {
		return this.model?.id || null;
	}

	// ------- props from / to ED record ------

	get objectType(): TeamSectionItemType {
		return this.getBuffered('objectType');
	}

	set objectType(value: TeamSectionItemType) {
		this.setBuffered('objectType', value);
	}

	// on metric type, this represents blockId
	get objectId(): number | null {
		return this.getBuffered('objectId');
	}

	set objectId(value: number | null) {
		this.setBuffered('objectId', value);
	}

	get objectData(): RecordData.TeamSectionItem.ObjectData {
		return this.getBuffered('objectData');
	}

	set objectData(value: RecordData.TeamSectionItem.ObjectData) {
		this.setBuffered('objectData', value);
	}

	get position(): number {
		return this.getBuffered('position');
	}

	set position(value: number) {
		this.setBuffered('position', value);
	}

	get sectionId(): number {
		return this.model.sectionId;
	}

	get dateRange(): DateRangeInterface.FormulaInterface {
		return this.getBuffered('dateRange');
	}

	set dateRange(value: DateRangeInterface.FormulaInterface) {
		this.setBuffered('dateRange', value);
	}

	get metricSetupKey(): string | null {
		return this.getBuffered('metricSetupKey');
	}

	set metricSetupKey(value: string) {
		this.setBuffered('metricSetupKey', value);
	}

	get metricSetupSpaceAccessId(): number | null {
		return this.getBuffered('metricSetupSpaceAccessId');
	}

	set metricSetupSpaceAccessId(value: number | null) {
		this.setBuffered('metricSetupSpaceAccessId', value);
	}

	get metricSetupDimensions(): string[] {
		return this.getBuffered('metricSetupDimensions');
	}

	set metricSetupDimensions(value: string[]) {
		this.setBuffered('metricSetupDimensions', value);
	}

	get metricSetupFilters(): DSSetup.MetricSetupFiltersData | null {
		return this.getBuffered('metricSetupFilters');
	}

	set metricSetupFilters(value: DSSetup.MetricSetupFiltersData | null) {
		this.setBuffered('metricSetupFilters', value);
	}

	// ------- type related props ------

	get isMetric() {
		return this.objectType === TeamSectionItemType.METRIC;
	}

	get isGoal() {
		return this.objectType === TeamSectionItemType.GOAL;
	}

	get isBoard() {
		return this.objectType === TeamSectionItemType.DATABOARD;
	}

	get isBoardLoop() {
		return this.objectType === TeamSectionItemType.DATABOARD_LOOP;
	}

	get isReport() {
		return this.objectType === TeamSectionItemType.REPORT;
	}

	@cached
	get stringId() {
		return this.objectId!.toString();
	}

	// ------- model related props ------

	@cached
	get boardModel(): Board | null {
		if (this.isBoard) {
			return this.mmBoardsService.findModel(this.stringId);
		}
		return null;
	}

	@cached
	get boardLoopModel(): BoardLoop | null {
		if (this.isBoardLoop) {
			return this.mmBoardLoopsService.findModel(this.stringId);
		}
		return null;
	}

	@cached
	get reportModel(): Report | null {
		if (this.isReport) {
			return this.mmReportsService.findModel(this.stringId);
		}
		return null;
	}

	@cached
	get goalModel(): Goal | null {
		if (this.isGoal) {
			return this.mmGoalsService.findModel(this.stringId);
		}
		return null;
	}

	get isItemLocked(): boolean {
		if (this.isReport) {
			return !this.billingService.reportsBasicEnabled;
		} else if (this.isGoal) {
			return !this.billingService.goalsEnabled;
		}

		return false;
	}

	get title(): string {
		if (this.isBoard) {
			return this.boardModel?.name ?? '';
		} else if (this.isBoardLoop) {
			return this.boardLoopModel?.name ?? '';
		} else if (this.isReport) {
			return this.reportModel?.title ?? '';
		} else if (this.isGoal) {
			return this.goalModel?.title ?? '';
		}

		return this.shownTitle;
	}

	get isRestricted() {
		// TODO: implement
		return false;
	}

	get section() {
		const id = this.sectionId.toString();
		return this.mmTeamSectionsService.findModel(id);
	}

	get team() {
		const id = this.section?.teamId.toString();
		if (id) {
			return this.mmTeamsService.findModel(id);
		}
		return null;
	}

	get isCurrentUserMember(): boolean {
		return !!this.team?.isCurrentUserMember;
	}

	updateObjectDataAndSave() {
		void this.mmTeamSectionItemsService.preparePayloadForUpdate(this).then((payload) => {
			this.objectData = payload;
			void this.save();
		});
	}

	// ------- metric related props ------

	@tracked private _block: Block | null = null;

	get context() {
		return {
			type: 'team-section-item',
			id: String(this.objectId),
		};
	}

	private _metricLimitations: MetricLimitations | null = null;

	@cached
	get limitations(): MetricLimitations {
		// re-creates limitations on metric, dateRange change
		let cachedLimitations = this._metricLimitations;

		if (cachedLimitations) {
			if (
				!equal(cachedLimitations.metric, this.metricSetup.metricData) ||
				!cachedLimitations.dateRange?.isEqual(this.dateRange)
			) {
				cachedLimitations = null;
			}
		}

		if (!cachedLimitations) {
			const dateRange = DateRange.createFromFormula(this.dateRange),
				metricData = this.metricSetup.metricData;

			cachedLimitations = new MetricLimitations({
				isNumber: this.isGoal,
				metrics: metricData ? [metricData] : [],
				dateRange,
			});

			void cachedLimitations.limitationsLoaded?.then(() => {
				// when creating the limitations model, it checks and resets incorrect granularities etc.
				// (checkValidityForAll called in _setupLimitationsDataAndObservers in constructor)
				// so we need to update the model back from the limitations
				// otherwise we fall into infinite limitations re-creation loop
				// THIS IS A TEMPORARY SOLUTION, we will not persist the change
				// we NEED to implement metric-limitations that can hold invalid state (task https://app.asana.com/0/0/1204813252988166/f)
				if (
					cachedLimitations?.dateRange &&
					!cachedLimitations.dateRange.isEqual(this.dateRange)
				) {
					this.dateRange = cachedLimitations.dateRange.formula;
				}
			});

			this._metricLimitations = cachedLimitations;
		}

		return cachedLimitations;
	}

	get blockId() {
		if (this.isMetric) {
			return this.objectId;
		}

		return null;
	}

	@cached
	get block() {
		const _block = this._block,
			blockId = String(this.blockId);

		// when we change metric, we need to find the new block, because blockId has changed and the old block is not valid anymore
		if ((!_block && blockId) || (_block && _block.id !== blockId)) {
			const block = this.mmBlocksService.findModelQuick(blockId);
			if (block) {
				next(() => {
					this._block = block;
					this.initializeVisualization();
				});
			} else {
				next(this, this.createAndSetFakeBlock);
			}
		}
		if (_block) {
			// updating block as necessary
			if (!this.limitations.dateRange?.isEqual(_block.dateRange)) {
				const formula = this.limitations.dateRange?.getFormula();
				if (formula) {
					_block.dateRange = formula;
				}
			}
		}

		return _block;
	}

	@cached
	get visualization() {
		return this.block?.blockSetup.visualization as VisualizationLineChart | undefined;
	}

	@cached
	get dataSelects(): DataSelect[] {
		return this.block?.dataSelects || [];
	}

	get isNumberSyncing(): boolean {
		const syncStatusObjects = this.block?.statusObjectsSync || [];
		return syncStatusObjects.filter((item) => item.dataLocation === 'number').length > 0;
	}

	get isLoadingData() {
		return !this.block?.data;
	}

	get lastSyncFromData() {
		return this.block?.lastSync || null;
	}

	get dataErrorStatusObjects() {
		return this.block?.statusObjectsErrors || [];
	}

	get showDataStatusIcon() {
		const block = this.block;
		return !!block && (!!block.lastSync || block.statusObjectsErrors.length > 0);
	}

	get numberPoint() {
		return this.visualization?.numberPoints[0] || null;
	}

	get comparePoint() {
		return this.numberPoint?.comparePoints[0] || null;
	}

	get hasNumberValue() {
		return !!this.numberPoint?.hasValue;
	}

	get numberValueStr() {
		this.timerService.observe();
		return this.numberPoint?.valueStrFormatted;
	}

	get changeString() {
		return this.comparePoint?.changeStr;
	}

	get previousValueString() {
		return this.comparePoint?.valueStr;
	}

	get indicatorClass() {
		return this.comparePoint?.indicatorClass as ChangeIndicatorClass;
	}

	get source(): Source | null {
		return this.metricSetup.source || null;
	}

	@cached
	get metricSetup() {
		const props: MetricSetupProps = {
			key: this.metricSetupKey,
			spaceAccessId: this.metricSetupSpaceAccessId,
			dimensions: this.metricSetupDimensions,
			filters: this.metricSetupFilters,
			allowAllUnits: false,
		};

		return new MetricSetup(props, this.handleMetricSetupUpdate.bind(this));
	}

	get hideNumberValue(): boolean {
		return this.isNumberSyncing && !this.hasNumberValue;
	}

	get shownTitle(): string {
		return this.metricSetup.selectedMetricTitle;
	}

	private createAndSetFakeBlock() {
		const blockId = this.blockId;
		if (blockId) {
			const block = this.mmBlocksService.findModelQuick(String(blockId));
			if (block) {
				this._block = block;
			} else {
				this._block = this.mmBlocksService.createNewModel({
					id: blockId,
					visualization: BlockVisualization.LINE_CHART,
					dateRange: copyObject(this.dateRange),
				});
			}
			this.initializeVisualization();
		}
	}

	private initializeVisualization() {
		const visualization = this.visualization!;
		visualization.showAnnotations = false;
		visualization.forceDChartRendererType = BlockVisualizationSpecial.METRIC_CHART;
		visualization.checkToUpdateDataCache();
	}

	private handleMetricSetupUpdate(
		prop: MetricSetupOnChangeProp,
		value: MetricSetupOnChangePropValue,
	) {
		switch (prop) {
			case 'key':
				this.metricSetupKey = value as string;
				break;
			case 'spaceAccessId':
				this.metricSetupSpaceAccessId = value as number | null;
				break;
			case 'dimensions':
				this.metricSetupDimensions = value as string[];
				break;
			case 'filters':
				this.metricSetupFilters = value as DSSetup.MetricSetupFiltersData;
				break;
		}
	}

	// ------- other props ------

	static override modelName = ModelName.TEAM_SECTION_ITEM; // store record type
}
